import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const CartSidebar = ({ 
  cartItems = [], 
  onUpdateQuantity, 
  onRemoveItem, 
  onCheckout,
  onToggleCart,
  isOpen = false,
  className = "" 
}) => {
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);

  const subtotal = cartItems?.reduce((sum, item) => sum + (item?.price * item?.quantity), 0);
  const shipping = subtotal > 100 ? 0 : 15;
  const discount = appliedPromo ? subtotal * 0.1 : 0;
  const tax = (subtotal - discount) * 0.08;
  const total = subtotal + shipping - discount + tax;

  const handleApplyPromo = () => {
    if (promoCode?.toLowerCase() === 'bike10') {
      setAppliedPromo({ code: 'BIKE10', discount: 0.1, description: '10% off your order' });
    } else {
      alert('Invalid promo code');
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoCode('');
  };

  if (!isOpen) {
    return (
      <div className={`fixed right-6 top-1/2 transform -translate-y-1/2 z-40 ${className}`}>
        <button
          onClick={onToggleCart}
          className="bg-accent text-accent-foreground p-3 rounded-full shadow-lg hover:bg-accent/90 transition-colors interactive-scale elevation-2"
        >
          <div className="relative">
            <Icon name="ShoppingCart" size={24} />
            {cartItems?.length > 0 && (
              <span className="absolute -top-2 -right-2 bg-error text-error-foreground text-xs w-5 h-5 rounded-full flex items-center justify-center">
                {cartItems?.length}
              </span>
            )}
          </div>
        </button>
      </div>
    );
  }

  return (
    <div className={`bg-card border border-border rounded-lg h-fit sticky top-24 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground">
          Upgrade Cart ({cartItems?.length})
        </h3>
        <button
          onClick={onToggleCart}
          className="p-1 hover:bg-muted rounded-full transition-colors interactive-scale"
        >
          <Icon name="X" size={20} />
        </button>
      </div>
      {cartItems?.length === 0 ? (
        <div className="p-6 text-center">
          <Icon name="ShoppingCart" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
          <p className="text-muted-foreground mb-4">Your cart is empty</p>
          <p className="text-sm text-muted-foreground">
            Add some upgrades to get started with your bike transformation!
          </p>
        </div>
      ) : (
        <>
          {/* Cart Items */}
          <div className="max-h-96 overflow-y-auto">
            {cartItems?.map((item) => (
              <div key={`${item?.id}-${item?.vendorId}`} className="p-4 border-b border-border/50">
                <div className="flex items-start space-x-3">
                  <Image
                    src={item?.image}
                    alt={item?.name}
                    className="w-12 h-12 rounded object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-foreground truncate">
                      {item?.name}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      from {item?.vendorName}
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => onUpdateQuantity(item?.id, item?.vendorId, Math.max(0, item?.quantity - 1))}
                          className="w-6 h-6 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors interactive-scale"
                        >
                          <Icon name="Minus" size={12} />
                        </button>
                        <span className="text-sm font-medium w-8 text-center">
                          {item?.quantity}
                        </span>
                        <button
                          onClick={() => onUpdateQuantity(item?.id, item?.vendorId, item?.quantity + 1)}
                          className="w-6 h-6 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors interactive-scale"
                        >
                          <Icon name="Plus" size={12} />
                        </button>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-foreground">
                          ${(item?.price * item?.quantity)?.toFixed(2)}
                        </div>
                        <button
                          onClick={() => onRemoveItem(item?.id, item?.vendorId)}
                          className="text-xs text-error hover:underline"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Promo Code */}
          <div className="p-4 border-b border-border">
            {appliedPromo ? (
              <div className="flex items-center justify-between bg-success/10 p-3 rounded-lg">
                <div>
                  <div className="text-sm font-medium text-success">
                    {appliedPromo?.code} Applied
                  </div>
                  <div className="text-xs text-success/80">
                    {appliedPromo?.description}
                  </div>
                </div>
                <button
                  onClick={handleRemovePromo}
                  className="text-success hover:text-success/80 transition-colors"
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    placeholder="Promo code"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e?.target?.value)}
                    className="flex-1 px-3 py-2 border border-input rounded-md text-sm focus:ring-2 focus:ring-ring focus:border-transparent"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleApplyPromo}
                    disabled={!promoCode?.trim()}
                  >
                    Apply
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Try code: BIKE10 for 10% off
                </p>
              </div>
            )}
          </div>

          {/* Order Summary */}
          <div className="p-4 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="text-foreground">${subtotal?.toFixed(2)}</span>
            </div>
            
            {discount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Discount</span>
                <span className="text-success">-${discount?.toFixed(2)}</span>
              </div>
            )}
            
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Shipping</span>
              <span className="text-foreground">
                {shipping === 0 ? 'Free' : `$${shipping?.toFixed(2)}`}
              </span>
            </div>
            
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Tax</span>
              <span className="text-foreground">${tax?.toFixed(2)}</span>
            </div>
            
            <div className="border-t border-border pt-3">
              <div className="flex justify-between text-base font-semibold">
                <span className="text-foreground">Total</span>
                <span className="text-foreground">${total?.toFixed(2)}</span>
              </div>
            </div>

            {subtotal < 100 && (
              <div className="bg-warning/10 p-3 rounded-lg">
                <p className="text-xs text-warning">
                  Add ${(100 - subtotal)?.toFixed(2)} more for free shipping!
                </p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="p-4 border-t border-border space-y-2">
            <Button
              variant="default"
              onClick={onCheckout}
              className="w-full"
              iconName="CreditCard"
              iconPosition="left"
            >
              Checkout (${total?.toFixed(2)})
            </Button>
            
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                iconName="Heart"
                iconPosition="left"
                onClick={() => {/* Save cart to wishlist */}}
              >
                Save to Wishlist
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                iconName="Share2"
                iconPosition="left"
                onClick={() => {/* Share cart */}}
              >
                Share Cart
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CartSidebar;